import React from 'react';
import { Calendar as CalendarIcon } from 'lucide-react';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

// Convert Date <-> "DD-MM-YYYY" string (the canonical format used in service module)
export const dateToStr = (d) => {
  if (!d) return '';
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  return `${day}-${month}-${d.getFullYear()}`;
};

export const strToDate = (s) => {
  if (!s) return undefined;
  const parts = String(s).split(/[-/]/);
  if (parts.length !== 3) return undefined;
  let d, m, y;
  if (parts[0].length === 4) { y = parts[0]; m = parts[1]; d = parts[2]; }
  else { d = parts[0]; m = parts[1]; y = parts[2]; if (y.length === 2) y = '20' + y; }
  const dt = new Date(`${y}-${m}-${d}T00:00:00`);
  return isNaN(dt.getTime()) ? undefined : dt;
};

const inputCx = 'w-full pl-9 pr-3 py-2.5 rounded-xl bg-white border border-slate-200 text-[13.5px] text-slate-900 placeholder-slate-400 focus:outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 transition-all';

/**
 * DateInput — date picker with:
 *  - Free-text typing in DD-MM-YYYY (canonical format)
 *  - Calendar popover (Monday week start, Clear/Today footer)
 *  - Always emits DD-MM-YYYY format on selection (consistent across views)
 */
export function DateInput({ value, onChange, placeholder = 'DD-MM-YYYY', disabled, testId, sizeCx = 'w-full' }) {
  const [open, setOpen] = React.useState(false);
  const selected = strToDate(value);

  const handleSelect = (d) => {
    if (d) onChange(dateToStr(d));
    setOpen(false);
  };

  const Footer = () => (
    <div className="flex items-center justify-between px-2 pt-2 mt-1 border-t border-slate-100">
      <button
        type="button"
        onClick={() => { onChange(''); setOpen(false); }}
        data-testid={testId ? `${testId}-clear` : undefined}
        className="text-[12.5px] font-medium text-sky-600 hover:text-sky-800 hover:underline px-2 py-1 rounded transition-colors"
      >
        Clear
      </button>
      <button
        type="button"
        onClick={() => handleSelect(new Date())}
        data-testid={testId ? `${testId}-today` : undefined}
        className="text-[12.5px] font-medium text-sky-600 hover:text-sky-800 hover:underline px-2 py-1 rounded transition-colors"
      >
        Today
      </button>
    </div>
  );

  return (
    <div className={`relative ${sizeCx}`}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            data-testid={testId ? `${testId}-icon` : undefined}
            className="absolute left-2 top-1/2 -translate-y-1/2 p-1 rounded-md text-slate-400 hover:text-sky-600 hover:bg-sky-50 transition-colors"
          >
            <CalendarIcon size={14} />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="p-0 w-auto rounded-xl shadow-xl border border-slate-200">
          <Calendar
            mode="single"
            selected={selected}
            onSelect={handleSelect}
            weekStartsOn={1}
            captionLayout="dropdown-buttons"
            fromYear={2020}
            toYear={2035}
            footer={<Footer />}
            initialFocus
          />
        </PopoverContent>
      </Popover>
      <input
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        data-testid={testId}
        className={inputCx}
      />
    </div>
  );
}

export default DateInput;
